import React from 'react'
import { createRoot } from 'react-dom/client'
import { sh, pt, compose, ReduxWrapper, buildPropTypes } from 'common-data/datahelpers'
import { componentGetCompareProps } from 'common-tools/index'
import FullCalendar from '@fullcalendar/react'
import dayGridPlugin from '@fullcalendar/daygrid'
import bootstrapPlugin from '@fullcalendar/bootstrap'
import momentPlugin from '@fullcalendar/moment'
import timeGridPlugin from '@fullcalendar/timegrid'
import interactionPlugin from '@fullcalendar/interaction'
import _ from 'common-tools/LodashHelper'
// NOTE: @fullcalendar/moment-timezone is intentionally excluded — it is incompatible
// with Vite's ESM pre-bundler (moment.tz is undefined at runtime in the pre-bundled chunk).
// Using timeZone='local' instead, which renders times in the user's browser timezone.

// ---- React 18 concurrent-mode patch for FullCalendar v4 ----
if (FullCalendar.prototype.componentWillUnmount) {
  const _origCWU = FullCalendar.prototype.componentWillUnmount
  FullCalendar.prototype.componentWillUnmount = function patchedFullCalendarCWU () {
    if (!this.calendar) return
    try { _origCWU.call(this) } catch (e) {
      console.warn('[FullCalendar v4 compat] componentWillUnmount error (React 18):', e.message)
    }
  }
}

// ---- Diagnostic: patch componentDidMount to trace initial render ----
{
  const _origCDM = FullCalendar.prototype.componentDidMount
  FullCalendar.prototype.componentDidMount = function patchedCDM () {
    const eventsInProps = Array.isArray(this.props.events) ? this.props.events.length : 'none'
    console.log('[FC wrapper] componentDidMount, events in props:', eventsInProps)
    _origCDM.call(this)
    const cal = this.calendar
    if (cal) {
      const store = cal.state.eventStore
      const instances = Object.keys(store?.instances || {}).length
      const loadingLevel = cal.state.eventSourceLoadingLevel
      console.log('[FC wrapper] after render(), events in store:', instances, 'loadingLevel:', loadingLevel)
      // Check if the view's DayGrid actually received segments
      const view = cal.component?.view
      if (view && view.simpleDayGrid) {
        const dg = view.simpleDayGrid.dayGrid
        const dgProps = dg?.props
        console.log('[FC wrapper] DayGrid fgEventSegs:', dgProps?.fgEventSegs?.length ?? 'N/A')
        // Check if eventRenderer has rowStructs (meaning segments were attached to DOM)
        const er = dg?.eventRenderer
        console.log('[FC wrapper] eventRenderer.rowStructs:', er?.rowStructs?.length ?? 'N/A',
          'rowEls:', dg?.rowEls?.length ?? 'N/A')
        if (er?.rowStructs) {
          er.rowStructs.forEach((rs, i) => {
            console.log('[FC wrapper] rowStruct[' + i + '] segs:', rs?.segs?.length ?? 0,
              'tbodyEl:', !!rs?.tbodyEl, 'tbodyEl.parentNode:', !!rs?.tbodyEl?.parentNode)
          })
        }
      }
      // Check actual DOM for event elements
      const el = cal.el
      const fcEvents = el?.querySelectorAll('.fc-event')
      const contentSkeletons = el?.querySelectorAll('.fc-content-skeleton')
      console.log('[FC wrapper] DOM: .fc-event count:', fcEvents?.length ?? 0,
        '.fc-content-skeleton count:', contentSkeletons?.length ?? 0)
      // After paint check
      requestAnimationFrame(() => {
        const fcEventsAfter = el?.querySelectorAll('.fc-event')
        console.log('[FC wrapper] rAF: .fc-event count:', fcEventsAfter?.length ?? 0)
        if (fcEventsAfter?.length > 0) {
          const first = fcEventsAfter[0]
          const style = window.getComputedStyle(first)
          console.log('[FC wrapper] first .fc-event: display:', style.display,
            'visibility:', style.visibility, 'opacity:', style.opacity,
            'offsetHeight:', first.offsetHeight, 'offsetWidth:', first.offsetWidth)
        }
      })
    }
  }
}

export const CalendarViewBoxViews = {
  Month: 'dayGridMonth',
  Week: 'timeGridWeek',
  Day: 'timeGridDay'
}

class CalendarViewBoxPlain extends React.Component {
    static propTypes = buildPropTypes([], {
      events: pt.array,
      disabled: pt.bool,
      loading: pt.bool,
      onCalendarMove: pt.func,
      onEventRender: pt.func,
      onDateClick: pt.func,
      businessHours: pt.shape({
        startTime: pt.any,
        endTime: pt.any
      }),
      headerButtons: pt.arrayOf(pt.shape({
        location: pt.string,
        action: pt.string,
        text: pt.string,
        icon: pt.string,
        onClick: pt.func
      })),
      clickSupport: pt.bool,
      selectAllow: pt.func,
      activeView: pt.shape({
        view: pt.string,
        date: pt.any
      })
    })

    static defaultProps = {
      events: [],
      headerButtons: [],
      clickSupport: false
    }

    calendarRef = React.createRef()
    state = { calendarKey: 0 }

    componentDidMount () {
      this._isMounted = true
    }

    componentWillUnmount () {
      this._isMounted = false
    }

    // ── Production build fix ─────────────────────────────────────────────
    // FC v4's DayGrid view + React 18 production builds: renderComponent()
    // fires with events in the store but the memoized DayGrid view doesn't
    // paint them.  Programmatic changeView() also fails (second changeView
    // doesn't trigger renderComponent at all).
    //
    // Fix: when events first arrive (empty → populated), force React to
    // fully unmount and remount FullCalendar by changing its `key`.  The new
    // instance's hydrate() processes `events` during initial render, which
    // always works correctly.  The key only changes ONCE via _hasReceivedEvents.
    _hasReceivedEvents = false
    _suppressDatesRender = false

    componentDidUpdate (prevProps, prevState) {
      // When events first arrive, force FullCalendar to remount
      const prevEvents = prevProps.events || []
      const currEvents = this.props.events || []
      console.log('[CalendarViewBox CDU] prevEvents:', prevEvents.length, 'currEvents:', currEvents.length,
        '_hasReceivedEvents:', this._hasReceivedEvents, 'calendarKey:', this.state.calendarKey)
      if (currEvents.length > 0 && prevEvents.length === 0 && !this._hasReceivedEvents) {
        console.log('[CalendarViewBox CDU] → triggering remount!')
        this._hasReceivedEvents = true
        // Suppress datesRender during remount to avoid duplicate API calls
        this._suppressDatesRender = true
        this.setState(
          prev => ({ calendarKey: prev.calendarKey + 1 }),
          () => {
            // Re-enable datesRender after remount completes
            setTimeout(() => { this._suppressDatesRender = false }, 0)
          }
        )
        return // skip activeView handling during remount
      }

      if (!this.calendarRef.current) return
      const calApi = this.calendarRef.current.calendar || null
      if (!calApi) return

      let activeView = componentGetCompareProps('activeView', this.props, prevProps, {})

      if (activeView.Current.view !== activeView.Previous.view) {
        calApi.changeView(activeView.Current.view, activeView.Current.date)
      } else if (activeView.Current.date !== activeView.Previous.date) {
        calApi.changeView(activeView.Current.view, activeView.Current.date)
      }
    }

    // ── Stable callback references (arrow class fields) ──────────────────
    // Using arrow class fields instead of .bind(this) in render().
    // This ensures FC v4's React wrapper (fast-deep-equal) sees the SAME
    // function reference every render → no spurious mutateOptions calls.

    _onMovePrev = () => {
      this._doCalendarMove('prev')
    }

    _onMoveNext = () => {
      this._doCalendarMove('next')
    }

    _onMoveToday = () => {
      this._doCalendarMove('today')
    }

    _doCalendarMove = (direction) => {
      const { onCalendarMove } = this.props
      if (_.isFunction(onCalendarMove) === false) {
        return
      }

      // Guard: ref may be null during rapid mount/unmount cycles (React 18)
      if (!this.calendarRef.current) return
      var cal = this.calendarRef.current.calendar || {}

      if (_.isFunction(cal[direction])) {
        cal[direction]()
        // NOTE: do NOT read cal.view here — it hasn't updated yet.
        // datesSet callback will fire after FullCalendar re-renders.
      }
    }

    _onDatesRender = (info) => {
      // Skip during programmatic view cycle to avoid extra API calls
      if (this._suppressDatesRender) return
      const { onCalendarMove } = this.props
      if (_.isFunction(onCalendarMove)) {
        onCalendarMove({ direction: 'datesRender', view: info.view })
      }
    }

    _onDateSelect = (selectInfo) => {
      const { onDateSelect } = this.props
      if (_.isFunction(onDateSelect) === false) {
        return
      }
      onDateSelect(selectInfo, this.calendarRef.current.calendar || {})
    }

    _onEventRender = (info) => {
      const { onEventRender } = this.props

      if (_.isFunction(onEventRender) === false) {
        return
      }

      let eventDetails = onEventRender(info.event, (info.view || {}).type)

      if (eventDetails) {
        if (eventDetails.body) {
          // Defer to next tick — cannot call createRoot synchronously inside
          // FullCalendar's componentDidUpdate (React 18 concurrent mode restriction).
          // CSS class and click handler are still applied synchronously below.
          const _el = info.el
          const _body = eventDetails.body
          setTimeout(() => {
            if (!_el._reactRoot) {
              _el._reactRoot = createRoot(_el)
            }
            _el._reactRoot.render(_body)
          }, 0)
        }

        if (eventDetails.css) {
          info.el.classList.add(eventDetails.css)
        }

        if (eventDetails.onClick) {
          info.el.addEventListener('click', event => {
            if (event.target && event.target.closest && (event.target.closest('.btn-danger') || event.target.closest('[data-prevent-drawer]'))) {
              event.stopPropagation()
              return
            }
            eventDetails.onClick(info.event)
          })
        }
      }
    }

    // ── Memoized config objects ───────────────────────────────────────────
    // Built once and cached so FC wrapper sees stable object references.
    _customButtons = null
    _header = null
    _plugins = null
    _select = null
    _selectAllow = null
    _configBuilt = false

    _buildConfig () {
      if (this._configBuilt) return

      const { headerButtons = [], clickSupport, selectAllow } = this.props

      this._customButtons = {
        movePrev: {
          text: 'prev',
          bootstrapFontAwesome: 'fa-chevron-left',
          click: this._onMovePrev
        },
        moveNext: {
          text: 'next',
          bootstrapFontAwesome: 'fa-chevron-right',
          click: this._onMoveNext
        },
        moveToday: {
          text: 'Today',
          click: this._onMoveToday
        }
      }

      this._header = {
        left: 'movePrev,moveNext moveToday ',
        center: 'title',
        right: ' dayGridMonth,timeGridWeek,timeGridDay'
      }

      if (headerButtons.length > 0) {
        for (let headerButton of headerButtons) {
          this._customButtons[headerButton.action] = {
            text: headerButton.text,
            bootstrapFontAwesome: `fa-${headerButton.icon || 'question'}`,
            click: headerButton.onClick
          }
          this._header[headerButton.location] = `${headerButton.action} ${this._header[headerButton.location]}`
        }
      }

      this._plugins = [dayGridPlugin, bootstrapPlugin, momentPlugin, timeGridPlugin]

      if (clickSupport === true) {
        this._plugins.push(interactionPlugin)
        this._select = this._onDateSelect
        // Optional guard — return false from selectAllow to reject a date span
        // before FullCalendar highlights it or fires select.
        this._selectAllow = _.isFunction(selectAllow) ? selectAllow : undefined
      }

      this._configBuilt = true
    }

    render () {
      const { events, businessHours, defaultView = 'dayGridMonth' } = this.props

      // Build config objects once (stable references for FC wrapper)
      this._buildConfig()

      return (

        <FullCalendar
          key={`cal-${this.state.calendarKey}`}
          ref={this.calendarRef}
          selectable
          selectMirror
          defaultView={defaultView}
          plugins={this._plugins}
          themeSystem='bootstrap'
          timeZone='local'
          progressiveEventRendering
          events={events}
          businessHours={businessHours}
          eventRender={this._onEventRender}
          datesRender={this._onDatesRender}
          customButtons={this._customButtons}
          header={this._header}
          select={this._select}
          selectAllow={this._selectAllow}
        />

      )
    }
}

export const CalendarViewBox = compose(
  ReduxWrapper([
    sh.SessionAccount
  ])
)(CalendarViewBoxPlain)

export default CalendarViewBox
